import { getPanelBrandSlug } from "@/config/config";

import { EProductType, TProduct } from "./products.type";
import { filterVehiclePlanAdjustmentsForForm } from "./schemas/auto.schema";
import {
  mergeCreditProductConfigFromApi,
} from "./schemas/credit-product.schema";
import { mergeTheftProductConfigFromApi } from "./schemas/theft-insurance.schema";
import {
  mergeAssistanceProductConfigFromApi,
  mergeWarrantyProductConfigFromApi,
} from "./schemas/warranty-insurance.schema";

// Converte de centavos (backend) para decimais com 2 casas (frontend - para inputs normais)
const toDecimal = (value: any): number | undefined => {
  if (value === null || value === undefined) return undefined;
  return Number(value) / 100;
};

// Converte de decimal (frontend) para centavos (backend - para inputs normais)
const toCents = (value: any): number | undefined => {
  if (value === null || value === undefined) return undefined;
  return Math.round(Number(value) * 100);
};

// Passa o valor direto (para campos que usam RealCurrencyInput - já em centavos)
const passThrough = (value: any): number | undefined => {
  if (value === null || value === undefined) return undefined;
  return Number(value);
};

function mapVehiclePlanAdjustmentsFromApi(raw: unknown) {
  const list = Array.isArray(raw) ? raw : [];
  return filterVehiclePlanAdjustmentsForForm(list as any).map((a: any) => ({
    ...a,
    new_product_value:
      a.new_product_value !== undefined && a.new_product_value !== null
        ? toDecimal(a.new_product_value)
        : undefined,
  }));
}

function mapVehiclePlanAdjustmentsToBackend(adj: unknown[]) {
  return (adj || []).map((a: any) => ({
    ...a,
    new_product_value:
      a.new_product_value === undefined || a.new_product_value === null
        ? undefined
        : toCents(a.new_product_value),
  }));
}

function mapAssistanceProductConfigToBackend(
  cfg: Record<string, unknown>,
): Record<string, unknown> {
  const c = cfg || {};
  return {
    ...c,
    net_price:
      c.net_price === undefined || c.net_price === null
        ? undefined
        : toCents(c.net_price),
    commissioned_price:
      c.commissioned_price === undefined || c.commissioned_price === null
        ? undefined
        : toCents(c.commissioned_price),
    pro_labore_value:
      c.pro_labore_value === undefined || c.pro_labore_value === null
        ? undefined
        : toCents(c.pro_labore_value),
    markup_enabled: Boolean(c.markup_enabled),
    markup_mode: c.markup_enabled ? c.markup_mode : undefined,
    markup_value:
      c.markup_enabled && c.markup_mode === "real_currency"
        ? c.markup_value === undefined || c.markup_value === null
          ? undefined
          : toCents(c.markup_value)
        : c.markup_enabled &&
            c.markup_value !== undefined &&
            c.markup_value !== null
          ? Number(c.markup_value)
          : undefined,
  };
}

export const personalAccidentToFrontend = (product: any): any => {
  return {
    ...product,
    iof_value: toDecimal(product.iof_value),
    markup: toDecimal(product.markup),
    commission_unique: toDecimal(product.commission_unique),
    pro_labore_unique: toDecimal(product.pro_labore_unique),
    commission_lifetime_other_installments: toDecimal(
      product.commission_lifetime_other_installments
    ),
    pro_labore_lifetime_other_installments: toDecimal(
      product.pro_labore_lifetime_other_installments
    ),

    capital_ranges: (product.capital_ranges || []).map((range: any) => ({
      ...range,
      capital_value: passThrough(range.capital_value),
      premium_value: passThrough(range.premium_value),
      net_premium: passThrough(range.net_premium),
      markup: toDecimal(range.markup),
      net_price: passThrough(range.net_price),
      minimum_price: passThrough(range.minimum_price),
      maximum_price: passThrough(range.maximum_price),
      practiced_price: passThrough(range.practiced_price),
      liquid_price: passThrough(range.liquid_price),
      gross_price: passThrough(range.gross_price),
      calculated_iof: passThrough(range.calculated_iof),
      iof_value: passThrough(range.iof_value),
    })),

    commission_distribution: (product.commission_distribution || []).map(
      (comm: any) => ({
        ...comm,
        agency: toDecimal(comm.agency),
        commission: toDecimal(comm.commission),
        pro_labore: toDecimal(comm.pro_labore),
        markup: toDecimal(comm.markup),
      })
    ),

    // Coberturas específicas de AP
    coverage_accident_death: toDecimal(product.coverage_accident_death),
    coverage_accident_disability: toDecimal(
      product.coverage_accident_disability
    ),
    coverage_medical_expenses: toDecimal(product.coverage_medical_expenses),
    coverage_funeral_assistance: toDecimal(product.coverage_funeral_assistance),
  };
};

export const personalAccidentToBackend = (data: any): any => {
  // Remove campos específicos do Auto que não devem existir para AP
  const { km_max_permitted, min_manufacturing_year, ...dataWithoutAuto } = data;
  
  // Remove campos undefined
  const cleanData = Object.fromEntries(
    Object.entries(dataWithoutAuto).filter(([_, value]) => value !== undefined)
  ) as any;
  
  return {
    ...cleanData,
    // Backend valida km_max_permitted e min_manufacturing_year como obrigatórios
    // Enviamos 0 como valor padrão para produtos AP
    km_max_permitted: 0,
    min_manufacturing_year: 0,
    iof_value: toCents(cleanData.iof_value) ?? 0,
    markup: toCents(cleanData.markup),
    commission_unique: toCents(cleanData.commission_unique) ?? 0,
    pro_labore_unique: toCents(cleanData.pro_labore_unique) ?? 0,
    commission_lifetime_other_installments: toCents(
      cleanData.commission_lifetime_other_installments
    ),
    pro_labore_lifetime_other_installments: toCents(
      cleanData.pro_labore_lifetime_other_installments
    ),

    capital_ranges: Array.isArray(cleanData.capital_ranges)
      ? cleanData.capital_ranges.map((range: any) => {
          const cleanRange = Object.fromEntries(
            Object.entries(range).filter(([_, value]) => value !== undefined)
          );

          return {
            ...cleanRange,
            // Campos com RealCurrencyInput - já em centavos, não converter
            capital_value: passThrough(cleanRange.capital_value),
            premium_value: passThrough(cleanRange.premium_value),
            net_premium: passThrough(cleanRange.net_premium),
            markup: toCents(cleanRange.markup),
            net_price: passThrough(cleanRange.net_price),
            minimum_price: passThrough(cleanRange.minimum_price),
            maximum_price: passThrough(cleanRange.maximum_price),
            practiced_price: passThrough(cleanRange.practiced_price),
            liquid_price: passThrough(cleanRange.liquid_price),
            gross_price: passThrough(cleanRange.gross_price),
            calculated_iof: passThrough(cleanRange.calculated_iof),
            iof_value: passThrough(cleanRange.iof_value),
          };
        })
      : cleanData.capital_ranges,

    commission_distribution: Array.isArray(cleanData.commission_distribution)
      ? cleanData.commission_distribution.map((comm: any) => {
          const cleanComm = Object.fromEntries(
            Object.entries(comm).filter(([_, value]) => value !== undefined)
          );

          return {
            ...cleanComm,
            agency: toCents(cleanComm.agency),
            commission: toCents(cleanComm.commission),
            pro_labore: toCents(cleanComm.pro_labore),
            markup: toCents(cleanComm.markup),
          };
        })
      : cleanData.commission_distribution,

    coverage_accident_death: toCents(cleanData.coverage_accident_death),
    coverage_accident_disability: toCents(
      cleanData.coverage_accident_disability
    ),
    coverage_medical_expenses: toCents(cleanData.coverage_medical_expenses),
    coverage_funeral_assistance: toCents(cleanData.coverage_funeral_assistance),
  };
};

export const groupLifeToFrontend = (product: any): any => {
  return {
    ...product,
    iof_value: toDecimal(product.iof_value),
    markup: toDecimal(product.markup),
    commission_unique: toDecimal(product.commission_unique),
    pro_labore_unique: toDecimal(product.pro_labore_unique),
    commission_lifetime_other_installments: toDecimal(
      product.commission_lifetime_other_installments
    ),
    pro_labore_lifetime_other_installments: toDecimal(
      product.pro_labore_lifetime_other_installments
    ),

    capital_ranges: (product.capital_ranges || []).map((range: any) => ({
      ...range,
      capital_value: passThrough(range.capital_value),
      premium_value: passThrough(range.premium_value),
      net_premium: passThrough(range.net_premium),
      markup: toDecimal(range.markup),
      net_price: passThrough(range.net_price),
      minimum_price: passThrough(range.minimum_price),
      maximum_price: passThrough(range.maximum_price),
      practiced_price: passThrough(range.practiced_price),
      liquid_price: passThrough(range.liquid_price),
      gross_price: passThrough(range.gross_price),
      calculated_iof: passThrough(range.calculated_iof),
      iof_value: passThrough(range.iof_value),
    })),

    commission_distribution: (product.commission_distribution || []).map(
      (comm: any) => ({
        ...comm,
        agency: toDecimal(comm.agency),
        commission: toDecimal(comm.commission),
        pro_labore: toDecimal(comm.pro_labore),
        markup: toDecimal(comm.markup),
      })
    ),

    coverage_death_natural_causes: toDecimal(
      product.coverage_death_natural_causes
    ),
    coverage_death_accident: toDecimal(product.coverage_death_accident),
    coverage_total_disability: toDecimal(product.coverage_total_disability),
    coverage_partial_disability: toDecimal(product.coverage_partial_disability),
    coverage_medical_expenses: toDecimal(product.coverage_medical_expenses),
  };
};

export const groupLifeToBackend = (data: any): any => {
  // Remove campos específicos do Auto que não devem existir para VG
  const { km_max_permitted, min_manufacturing_year, ...dataWithoutAuto } = data;
  
  // Remove campos undefined
  const cleanData = Object.fromEntries(
    Object.entries(dataWithoutAuto).filter(([_, value]) => value !== undefined)
  ) as any;

  return {
    ...cleanData,
    // Backend valida km_max_permitted e min_manufacturing_year como obrigatórios
    // Enviamos 0 como valor padrão para produtos VG
    km_max_permitted: 0,
    min_manufacturing_year: 0,
    iof_value: toCents(cleanData.iof_value) ?? 0,
    markup: toCents(cleanData.markup),
    commission_unique: toCents(cleanData.commission_unique) ?? 0,
    pro_labore_unique: toCents(cleanData.pro_labore_unique) ?? 0,
    commission_lifetime_other_installments: toCents(
      cleanData.commission_lifetime_other_installments
    ),
    pro_labore_lifetime_other_installments: toCents(
      cleanData.pro_labore_lifetime_other_installments
    ),

    capital_ranges: Array.isArray(cleanData.capital_ranges)
      ? cleanData.capital_ranges.map((range: any) => {
          const cleanRange = Object.fromEntries(
            Object.entries(range).filter(([_, value]) => value !== undefined)
          );

          return {
            ...cleanRange,
            capital_value: passThrough(cleanRange.capital_value),
            premium_value: passThrough(cleanRange.premium_value),
            net_premium: passThrough(cleanRange.net_premium),
            markup: toCents(cleanRange.markup),
            net_price: passThrough(cleanRange.net_price),
            minimum_price: passThrough(cleanRange.minimum_price),
            maximum_price: passThrough(cleanRange.maximum_price),
            practiced_price: passThrough(cleanRange.practiced_price),
            liquid_price: passThrough(cleanRange.liquid_price),
            gross_price: passThrough(cleanRange.gross_price),
            calculated_iof: passThrough(cleanRange.calculated_iof),
            iof_value: passThrough(cleanRange.iof_value),
          };
        })
      : cleanData.capital_ranges,

    commission_distribution: Array.isArray(cleanData.commission_distribution)
      ? cleanData.commission_distribution.map((comm: any) => {
          const cleanComm = Object.fromEntries(
            Object.entries(comm).filter(([_, value]) => value !== undefined)
          );

          return {
            ...cleanComm,
            agency: toCents(cleanComm.agency),
            commission: toCents(cleanComm.commission),
            pro_labore: toCents(cleanComm.pro_labore),
            markup: toCents(cleanComm.markup),
          };
        })
      : cleanData.commission_distribution,

    coverage_death_natural_causes: toCents(
      cleanData.coverage_death_natural_causes
    ),
    coverage_death_accident: toCents(cleanData.coverage_death_accident),
    coverage_total_disability: toCents(cleanData.coverage_total_disability),
    coverage_partial_disability: toCents(cleanData.coverage_partial_disability),
    coverage_medical_expenses: toCents(cleanData.coverage_medical_expenses),
  };
};

export const assistanceToFrontend = (product: any): any => {
  return {
    ...product,
    iof_value: toDecimal(product.iof_value),
    markup: toDecimal(product.markup),
    commission_unique: toDecimal(product.commission_unique),
    pro_labore_unique: toDecimal(product.pro_labore_unique),
    commission_lifetime_other_installments: toDecimal(
      product.commission_lifetime_other_installments
    ),
    pro_labore_lifetime_other_installments: toDecimal(
      product.pro_labore_lifetime_other_installments
    ),
    assistance_product_config: mergeAssistanceProductConfigFromApi(
      product.assistance_product_config,
    ),

    capital_ranges: (product.capital_ranges || []).map((range: any) => ({
      ...range,
      capital_value: passThrough(range.capital_value),
      premium_value: passThrough(range.premium_value),
      net_premium: passThrough(range.net_premium),
      markup: toDecimal(range.markup),
      net_price: passThrough(
        range.net_price !== undefined && range.net_price !== null
          ? range.net_price
          : range.premium_value,
      ),
      minimum_price: passThrough(range.minimum_price),
      maximum_price: passThrough(range.maximum_price),
      practiced_price: passThrough(range.practiced_price),
      liquid_price: passThrough(range.liquid_price),
      gross_price: passThrough(range.gross_price),
      calculated_iof: passThrough(range.calculated_iof),
      iof_value: passThrough(range.iof_value),
      plan_name: range.plan_name || "",
      code_plan: range.code_plan || "",
      description: range.description || "",
      new_coverages: range.new_coverages ?? "",
      plan_adjustments: mapVehiclePlanAdjustmentsFromApi(range.plan_adjustments),
    })),

    commission_distribution: (product.commission_distribution || []).map(
      (comm: any) => ({
        ...comm,
        agency: toDecimal(comm.agency),
        commission: toDecimal(comm.commission),
        pro_labore: toDecimal(comm.pro_labore),
        markup: toDecimal(comm.markup),
      })
    ),

    coverage_medical_assistance: toDecimal(product.coverage_medical_assistance),
    coverage_dental_assistance: toDecimal(product.coverage_dental_assistance),
    coverage_pharmaceutical_assistance: toDecimal(
      product.coverage_pharmaceutical_assistance
    ),
    coverage_hospital_assistance: toDecimal(
      product.coverage_hospital_assistance
    ),
    coverage_emergency_assistance: toDecimal(
      product.coverage_emergency_assistance
    ),
    coverage_funeral_assistance: toDecimal(product.coverage_funeral_assistance),

    annual_limit_per_person: toDecimal(product.annual_limit_per_person),
    monthly_limit_per_person: toDecimal(product.monthly_limit_per_person),
  };
};

export const assistanceToBackend = (data: any): any => {
  const {
    km_max_permitted,
    min_manufacturing_year,
    assistance_product_config,
    ...rest
  } = data;

  const cleanData = Object.fromEntries(
    Object.entries(rest).filter(([_, value]) => value !== undefined),
  ) as any;

  const out: any = {
    ...cleanData,
    km_max_permitted: 0,
    min_manufacturing_year: 0,
    iof_value: toCents(cleanData.iof_value) ?? 0,
    markup: toCents(cleanData.markup),
    commission_unique: toCents(cleanData.commission_unique) ?? 0,
    pro_labore_unique: toCents(cleanData.pro_labore_unique) ?? 0,
    commission_lifetime_other_installments: toCents(
      cleanData.commission_lifetime_other_installments,
    ),
    pro_labore_lifetime_other_installments: toCents(
      cleanData.pro_labore_lifetime_other_installments,
    ),

    capital_ranges: Array.isArray(cleanData.capital_ranges)
      ? cleanData.capital_ranges.map((range: any) => {
          const cleanRange = Object.fromEntries(
            Object.entries(range).filter(([_, value]) => value !== undefined),
          );
          const premiumFromForm =
            cleanRange.net_price ?? cleanRange.premium_value;

          return {
            ...cleanRange,
            capital_value: passThrough(cleanRange.capital_value),
            net_premium: passThrough(cleanRange.net_premium),
            markup: toCents(cleanRange.markup),
            premium_value: passThrough(premiumFromForm) ?? 0,
            minimum_price: passThrough(cleanRange.minimum_price),
            maximum_price: passThrough(cleanRange.maximum_price),
            practiced_price: passThrough(cleanRange.practiced_price),
            liquid_price: passThrough(cleanRange.liquid_price),
            gross_price: passThrough(cleanRange.gross_price),
            calculated_iof: passThrough(cleanRange.calculated_iof),
            iof_value: passThrough(cleanRange.iof_value),
            plan_name: cleanRange.plan_name || "",
            code_plan: cleanRange.code_plan || "",
            description: cleanRange.description || "",
            new_coverages: cleanRange.new_coverages ?? "",
            plan_adjustments: mapVehiclePlanAdjustmentsToBackend(
              Array.isArray(cleanRange.plan_adjustments)
                ? cleanRange.plan_adjustments
                : [],
            ),
          };
        })
      : cleanData.capital_ranges,

    commission_distribution: Array.isArray(cleanData.commission_distribution)
      ? cleanData.commission_distribution.map((comm: any) => {
          const cleanComm = Object.fromEntries(
            Object.entries(comm).filter(([_, value]) => value !== undefined),
          );

          return {
            ...cleanComm,
            agency: toCents(cleanComm.agency),
            commission: toCents(cleanComm.commission),
            pro_labore: toCents(cleanComm.pro_labore),
            markup: toCents(cleanComm.markup),
          };
        })
      : cleanData.commission_distribution,

    coverage_medical_assistance: toCents(cleanData.coverage_medical_assistance),
    coverage_dental_assistance: toCents(cleanData.coverage_dental_assistance),
    coverage_pharmaceutical_assistance: toCents(
      cleanData.coverage_pharmaceutical_assistance,
    ),
    coverage_hospital_assistance: toCents(
      cleanData.coverage_hospital_assistance,
    ),
    coverage_emergency_assistance: toCents(
      cleanData.coverage_emergency_assistance,
    ),
    coverage_funeral_assistance: toCents(cleanData.coverage_funeral_assistance),

    annual_limit_per_person: toCents(cleanData.annual_limit_per_person),
    monthly_limit_per_person: toCents(cleanData.monthly_limit_per_person),
  };

  if (
    assistance_product_config &&
    typeof assistance_product_config === "object"
  ) {
    out.assistance_product_config = mapAssistanceProductConfigToBackend(
      assistance_product_config as Record<string, unknown>,
    );
  }

  return out;
};

export const autoToFrontend = (product: any): any => {
  const result: any = {
    ...product,
    prefixed_premium_value: toDecimal(product.prefixed_premium_value),
    iof_value: toDecimal(product.iof_value),
    markup: toDecimal(product.markup),
    commission_unique: toDecimal(product.commission_unique),
    pro_labore_unique: toDecimal(product.pro_labore_unique),
    commission_lifetime_other_installments: toDecimal(
      product.commission_lifetime_other_installments
    ),
    pro_labore_lifetime_other_installments: toDecimal(
      product.pro_labore_lifetime_other_installments
    ),

    commission_distribution: (product.commission_distribution || []).map(
      (comm: any) => ({
        ...comm,
        agency: toDecimal(comm.agency),
        commission: toDecimal(comm.commission),
        pro_labore: toDecimal(comm.pro_labore),
        markup: toDecimal(comm.markup),
      })
    ),
  };

  // Converter capital_ranges para a nova estrutura de plans (se existir)
  // DESABILITADO: O formulário agora trabalha diretamente com capital_ranges
  /*
  if (Array.isArray(product.capital_ranges) && product.capital_ranges.length > 0) {
    result.plans = product.capital_ranges.map((range: any) => {
      ... código removido ...
    });
  }
  */

  // Manter capital_ranges para o formulário usar diretamente
  if (Array.isArray(product.capital_ranges) && product.capital_ranges.length > 0) {
    result.capital_ranges = (product.capital_ranges || []).map((range: any) => {
      
      // Manter TODOS os campos do range, seguindo o padrão dos outros produtos
      const mapped = {
        ...range,
        capital_value: passThrough(range.capital_value),
        premium_value: passThrough(range.premium_value),
        net_premium: passThrough(range.net_premium),
        markup: toDecimal(range.markup),
        net_price: passThrough(range.net_price),
        minimum_price: passThrough(range.minimum_price),
        maximum_price: passThrough(range.maximum_price),
        practiced_price: passThrough(range.practiced_price),
        liquid_price: passThrough(range.liquid_price),
        gross_price: passThrough(range.gross_price),
        calculated_iof: passThrough(range.calculated_iof),
        iof_value: passThrough(range.iof_value),
        // commission_auto não precisa de conversão, já vem como string/number do backend
        commission_auto: range.commission_auto !== null && range.commission_auto !== undefined 
          ? Number(range.commission_auto) 
          : undefined,
      };
      return mapped;
    });
  } else {
    result.capital_ranges = [];
  }

  return result;
};

export const creditToFrontend = (product: TProduct): any => {
  return {
    ...product,
    credit_product_config: mergeCreditProductConfigFromApi(
      (product as { credit_product_config?: unknown }).credit_product_config,
    ),
  };
};

export const warrantyToFrontend = (product: TProduct): any => {
  return {
    ...product,
    iof_value: toDecimal(product.iof_value),
    policy_start_date: product.policy_start_date
      ? new Date(product.policy_start_date)
      : undefined,
    policy_end_date: product.policy_end_date
      ? new Date(product.policy_end_date)
      : undefined,
    warranty_product_config: mergeWarrantyProductConfigFromApi(
      (product as { warranty_product_config?: unknown }).warranty_product_config,
    ),
  };
};

export const warrantyToBackend = (data: any): any => {
  const cfg = data.warranty_product_config || {};
  const mapAdjustments = (adj: any[]) =>
    (adj || []).map((a) => ({
      ...a,
      new_assistance_value:
        a.new_assistance_value === undefined || a.new_assistance_value === null
          ? undefined
          : toCents(a.new_assistance_value),
    }));

  return {
    type: EProductType.WARRANTY_INSURANCE,
    name: data.name,
    insurance_company_id: data.insurance_company_id,
    policy_number: data.policy_number || undefined,
    susep_code: data.susep_code || undefined,
    iof_value: toCents(data.iof_value) ?? 0,
    type_of_vigency: data.type_of_vigency,
    policy_start_date: data.policy_start_date || undefined,
    policy_end_date: data.policy_end_date || undefined,
    min_permitted_age:
      data.min_permitted_age === null || data.min_permitted_age === undefined
        ? undefined
        : Number(data.min_permitted_age),
    max_permitted_age:
      data.max_permitted_age === null || data.max_permitted_age === undefined
        ? undefined
        : Number(data.max_permitted_age),
    description: data.description || undefined,
    insurance_coverages: data.insurance_coverages || undefined,
    general_conditions: data.general_conditions || undefined,
    excluded_risks: data.excluded_risks || undefined,
    infinity_sync_as_source: data.infinity_sync_as_source ?? true,
    warranty_product_config: {
      ...cfg,
      net_price:
        cfg.net_price === undefined || cfg.net_price === null
          ? undefined
          : toCents(cfg.net_price),
      commissioned_price:
        cfg.commissioned_price === undefined || cfg.commissioned_price === null
          ? undefined
          : toCents(cfg.commissioned_price),
      pro_labore_value:
        cfg.pro_labore_value === undefined || cfg.pro_labore_value === null
          ? undefined
          : toCents(cfg.pro_labore_value),
      markup_enabled: Boolean(cfg.markup_enabled),
      markup_mode: cfg.markup_enabled ? cfg.markup_mode : undefined,
      markup_value:
        cfg.markup_enabled && cfg.markup_mode === "real_currency"
          ? cfg.markup_value === undefined || cfg.markup_value === null
            ? undefined
            : toCents(cfg.markup_value)
          : cfg.markup_enabled && cfg.markup_value !== undefined && cfg.markup_value !== null
            ? Number(cfg.markup_value)
            : undefined,
      plans: (cfg.plans || []).map((p: any) => ({
        ...p,
        adjustments: mapAdjustments(p.adjustments || []),
      })),
    },
    capital_ranges: [],
    commission_distribution: [],
  };
};

export const theftToFrontend = (product: TProduct): any => {
  return {
    ...product,
    iof_value: toDecimal(product.iof_value),
    policy_start_date: product.policy_start_date
      ? new Date(product.policy_start_date)
      : undefined,
    policy_end_date: product.policy_end_date
      ? new Date(product.policy_end_date)
      : undefined,
    theft_product_config: mergeTheftProductConfigFromApi(
      (product as { theft_product_config?: unknown }).theft_product_config,
    ),
  };
};

export const theftToBackend = (data: any): any => {
  const cfg = data.theft_product_config || {};
  const mapAdjustments = (adj: any[]) =>
    (adj || []).map((a) => ({
      ...a,
      new_assistance_value:
        a.new_assistance_value === undefined || a.new_assistance_value === null
          ? undefined
          : toCents(a.new_assistance_value),
    }));

  return {
    type: EProductType.THEFT_INSURANCE,
    name: data.name,
    insurance_company_id: data.insurance_company_id,
    policy_number: data.policy_number || undefined,
    susep_code: data.susep_code || undefined,
    iof_value: toCents(data.iof_value) ?? 0,
    type_of_vigency: data.type_of_vigency,
    policy_start_date: data.policy_start_date || undefined,
    policy_end_date: data.policy_end_date || undefined,
    min_permitted_age:
      data.min_permitted_age === null || data.min_permitted_age === undefined
        ? undefined
        : Number(data.min_permitted_age),
    max_permitted_age:
      data.max_permitted_age === null || data.max_permitted_age === undefined
        ? undefined
        : Number(data.max_permitted_age),
    description: data.description || undefined,
    insurance_coverages: data.insurance_coverages || undefined,
    general_conditions: data.general_conditions || undefined,
    excluded_risks: data.excluded_risks || undefined,
    infinity_sync_as_source: data.infinity_sync_as_source ?? true,
    theft_product_config: {
      ...cfg,
      net_price:
        cfg.net_price === undefined || cfg.net_price === null
          ? undefined
          : toCents(cfg.net_price),
      commissioned_price:
        cfg.commissioned_price === undefined || cfg.commissioned_price === null
          ? undefined
          : toCents(cfg.commissioned_price),
      pro_labore_value:
        cfg.pro_labore_value === undefined || cfg.pro_labore_value === null
          ? undefined
          : toCents(cfg.pro_labore_value),
      markup_enabled: Boolean(cfg.markup_enabled),
      markup_mode: cfg.markup_enabled ? cfg.markup_mode : undefined,
      markup_value:
        cfg.markup_enabled && cfg.markup_mode === "real_currency"
          ? cfg.markup_value === undefined || cfg.markup_value === null
            ? undefined
            : toCents(cfg.markup_value)
          : cfg.markup_enabled && cfg.markup_value !== undefined && cfg.markup_value !== null
            ? Number(cfg.markup_value)
            : undefined,
      plans: (cfg.plans || []).map((p: any) => ({
        ...p,
        adjustments: mapAdjustments(p.adjustments || []),
      })),
    },
    capital_ranges: [],
    commission_distribution: [],
  };
};

export const creditToBackend = (data: any): any => {
  const out: Record<string, unknown> = {
    name: data.name,
    type: EProductType.CREDIT,
    credit_product_config: data.credit_product_config,
    capital_ranges: [],
    commission_distribution: [],
    infinity_sync_as_source: data.infinity_sync_as_source ?? true,
  };
  if (data.insurance_company_id) {
    out.insurance_company_id = data.insurance_company_id;
  }
  return out;
};

export const mapProductToFrontend = (product: TProduct): any => {
  switch (product.type) {
    case EProductType.INSURANCE_AP:
      return personalAccidentToFrontend(product);
    case EProductType.INSURANCE_VG:
      return groupLifeToFrontend(product);
    case EProductType.INSURANCE_ASSISTANCE:
      return assistanceToFrontend(product);
    case EProductType.AUTO:
    case EProductType.MOTO:
      return autoToFrontend(product);
    case EProductType.CREDIT:
      return creditToFrontend(product);
    case EProductType.WARRANTY_INSURANCE:
      return warrantyToFrontend(product);
    case EProductType.THEFT_INSURANCE:
      return theftToFrontend(product);
    default:
      return product;
  }
};

export const autoToBackend = (data: any): any => {
  // Remove campos de idade que não devem existir para Auto
  const { min_permitted_age, max_permitted_age, ...dataWithoutAge } = data;

  // Remove undefined e strings vazias (campos opcionais do Auto)
  const cleanData = Object.fromEntries(
    Object.entries(dataWithoutAge).filter(([key, value]) => {
      // Remove campos opcionais vazios de Auto
      if (['policy_number', 'susep_code', 'cod_sub'].includes(key)) {
        return value !== undefined && value !== null && value !== '';
      }
      // Para outros campos, apenas remove undefined
      return value !== undefined;
    })
  ) as any;

  const result: any = {
    ...cleanData,
    // Backend valida min_permitted_age e max_permitted_age como obrigatórios
    // Enviamos 0 como valor padrão para produtos Auto
    min_permitted_age: 0,
    max_permitted_age: 0,
    iof_value: toCents(cleanData.iof_value) ?? 0,
    markup: toCents(cleanData.markup),
    commission_unique: toCents(cleanData.commission_unique) ?? 0,
    pro_labore_unique: toCents(cleanData.pro_labore_unique) ?? 0,
    commission_lifetime_other_installments: toCents(
      cleanData.commission_lifetime_other_installments
    ),
    pro_labore_lifetime_other_installments: toCents(
      cleanData.pro_labore_lifetime_other_installments
    ),
  };

  // Campos específicos do Auto - só incluir se tiverem valores válidos
  if (cleanData.km_max_permitted !== undefined && cleanData.km_max_permitted !== null) {
    result.km_max_permitted = cleanData.km_max_permitted;
  }
  if (cleanData.min_manufacturing_year !== undefined && cleanData.min_manufacturing_year !== null) {
    result.min_manufacturing_year = cleanData.min_manufacturing_year;
  }

  // Processar planos de Auto (nova estrutura)
  if (cleanData.has_multiple_plans === false) {
    result.capital_ranges = [];
  } else if (Array.isArray(cleanData.plans) && cleanData.plans.length > 0) {
    result.capital_ranges = cleanData.plans.map((plan: any) => {
      return {
        capital_value: 0,
        premium_value: 0,
        plan_name: plan.name || "",
        description: plan.description || "",
        new_coverages: plan.new_coverages || "",
        plan_adjustments: Array.isArray(plan.plan_adjustments)
          ? plan.plan_adjustments
          : [],
        markup_mode: "percentage",
      };
    });
  } else {
    // Fallback para capital_ranges tradicional (caso exista)
    result.capital_ranges = Array.isArray(cleanData.capital_ranges)
      ? cleanData.capital_ranges.map((range: any) => {
          
          const cleanRange = Object.fromEntries(
            Object.entries(range).filter(([_, value]) => value !== undefined)
          );

          // Seguir o mesmo padrão dos outros produtos
          return {
            ...cleanRange,
            capital_value: passThrough(cleanRange.capital_value),
            premium_value: passThrough(cleanRange.premium_value),
            net_premium: passThrough(cleanRange.net_premium),
            markup: toCents(cleanRange.markup),
            net_price: passThrough(cleanRange.net_price),
            minimum_price: passThrough(cleanRange.minimum_price),
            maximum_price: passThrough(cleanRange.maximum_price),
            practiced_price: passThrough(cleanRange.practiced_price),
            liquid_price: passThrough(cleanRange.liquid_price),
            gross_price: passThrough(cleanRange.gross_price),
            calculated_iof: passThrough(cleanRange.calculated_iof),
            iof_value: passThrough(cleanRange.iof_value),
            // commission_auto não precisa conversão, é porcentagem simples
            commission_auto: cleanRange.commission_auto,
          };
        })
      : cleanData.capital_ranges;
  }

  result.commission_distribution = Array.isArray(cleanData.commission_distribution)
    ? cleanData.commission_distribution.map((comm: any) => {
        const cleanComm = Object.fromEntries(
          Object.entries(comm).filter(([_, value]) => value !== undefined)
        );

        return {
          ...cleanComm,
          agency: toCents(cleanComm.agency),
          commission: toCents(cleanComm.commission),
          pro_labore: toCents(cleanComm.pro_labore),
          markup: toCents(cleanComm.markup),
        };
      })
    : cleanData.commission_distribution;

  const vehicleScalarKeys = [
    "vehicle_emission_type",
    "is_collective_policy",
    "has_estipulante",
    "has_sub_estipulante",
    "susep_product_code",
    "has_km_limit",
    "has_manufacturing_year_limit",
    "max_vehicle_age",
    "excluded_risks",
    "premium_mode",
    "has_multiple_plans",
    "vehicle_product_config",
  ] as const;
  for (const k of vehicleScalarKeys) {
    if (cleanData[k] !== undefined) {
      (result as Record<string, unknown>)[k] = cleanData[k];
    }
  }
  if (cleanData.prefixed_premium_value !== undefined) {
    result.prefixed_premium_value = toCents(cleanData.prefixed_premium_value);
  }

  delete result.plans;

  return result;
};

export const mapProductToBackend = (data: any): any => {
  const type = data.type;

  let result: any;
  switch (type) {
    case EProductType.INSURANCE_AP:
      result = personalAccidentToBackend(data);
      break;
    case EProductType.INSURANCE_VG:
      result = groupLifeToBackend(data);
      break;
    case EProductType.INSURANCE_ASSISTANCE:
      result = assistanceToBackend(data);
      break;
    case EProductType.AUTO:
    case EProductType.MOTO:
      result = autoToBackend(data);
      break;
    case EProductType.CREDIT:
      result = creditToBackend(data);
      break;
    case EProductType.WARRANTY_INSURANCE:
      result = warrantyToBackend(data);
      break;
    case EProductType.THEFT_INSURANCE:
      result = theftToBackend(data);
      break;
    default:
      result = data;
  }

  if (getPanelBrandSlug() !== "soulhub") {
    return result;
  }
  const raw = data.external_product_id;
  if (typeof raw === "string" && raw.trim() !== "") {
    return { ...result, external_product_id: raw.trim() };
  }
  return result;
};
